import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import { ChevronDown, Info, CheckCircle2, AlertTriangle, XCircle, Quote } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '../components/ui/carousel';
import { Progress } from '../components/ui/progress';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Block Renderers ──────────────────────────────────────────────

function BlockHeading({ data }) {
  const Tag = data.level || 'h2';
  const sizeMap = { h1: 'text-5xl', h2: 'text-4xl', h3: 'text-2xl', h4: 'text-xl' };
  const alignMap = { left: 'text-left', center: 'text-center', right: 'text-right' };
  return (
    <Tag
      className={`font-black leading-tight ${sizeMap[data.level] || 'text-4xl'} ${alignMap[data.align] || 'text-left'}`}
      style={{ color: data.color || '#ffffff', fontFamily: 'Outfit, sans-serif' }}
    >
      {data.text}
    </Tag>
  );
}

function BlockParagraph({ data }) {
  const alignMap = { left: 'text-left', center: 'text-center', right: 'text-right' };
  return (
    <p className={`text-ink/75 leading-relaxed text-base whitespace-pre-wrap ${alignMap[data.align] || 'text-left'}`}>
      {data.content}
    </p>
  );
}

function BlockImage({ data }) {
  const sizeMap = { full: 'w-full', contained: 'max-w-3xl mx-auto w-full', small: 'max-w-sm mx-auto w-full' };
  return (
    <figure className={sizeMap[data.size] || 'max-w-3xl mx-auto w-full'}>
      {data.src && (
        <img
          src={data.src}
          alt={data.alt || ''}
          className="w-full rounded-2xl border border-ink/10 object-cover"
        />
      )}
      {data.caption && (
        <figcaption className="text-ink/55 text-sm text-center mt-2">{data.caption}</figcaption>
      )}
    </figure>
  );
}

function BlockImageText({ data }) {
  const isLeft = (data.image_side || 'left') === 'left';
  return (
    <div className={`flex flex-col md:flex-row gap-8 md:gap-12 items-center ${!isLeft ? 'md:flex-row-reverse' : ''}`}>
      {data.image_src && (
        <div className="w-full md:w-1/2 flex-shrink-0">
          <img
            src={data.image_src}
            alt={data.image_alt || ''}
            className="w-full rounded-2xl border border-ink/10 object-cover"
          />
        </div>
      )}
      <div className="w-full md:w-1/2 space-y-4">
        {data.title && (
          <h3 className="text-2xl font-black text-ink" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {data.title}
          </h3>
        )}
        {data.content && (
          <p className="text-ink/75 leading-relaxed whitespace-pre-wrap">{data.content}</p>
        )}
      </div>
    </div>
  );
}

function BlockVideo({ data }) {
  if (!data.url) return null;
  const getEmbedUrl = (url) => {
    const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    if (yt) return `https://www.youtube.com/embed/${yt[1]}${data.autoplay ? '?autoplay=1' : ''}`;
    const vimeo = url.match(/vimeo\.com\/(\d+)/);
    if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}${data.autoplay ? '?autoplay=1' : ''}`;
    return url;
  };
  return (
    <figure className="w-full max-w-3xl mx-auto">
      <div className="relative w-full rounded-2xl overflow-hidden border border-ink/10" style={{ paddingTop: '56.25%' }}>
        <iframe
          src={getEmbedUrl(data.url)}
          title="Video"
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
      {data.caption && (
        <figcaption className="text-ink/55 text-sm text-center mt-2">{data.caption}</figcaption>
      )}
    </figure>
  );
}

function BlockCTA({ data }) {
  const alignMap = { left: 'text-left items-start', center: 'text-center items-center', right: 'text-right items-end' };
  return (
    <div
      className="rounded-2xl px-8 py-12 flex flex-col gap-4"
      style={{
        backgroundColor: data.bg_color || '#00E5FF',
        color: data.text_color || '#000000',
        alignItems: data.align === 'left' ? 'flex-start' : data.align === 'right' ? 'flex-end' : 'center',
      }}
    >
      <h3 className={`text-3xl font-black ${alignMap[data.align || 'center']?.split(' ')[0] || 'text-center'}`}
        style={{ fontFamily: 'Outfit, sans-serif', color: 'inherit' }}>
        {data.title}
      </h3>
      {data.subtitle && (
        <p className="text-base opacity-80">{data.subtitle}</p>
      )}
      {data.button_text && (
        <Link
          to={data.button_link || '/'}
          className="mt-2 px-8 py-3 rounded-full font-bold text-sm transition-all hover:scale-105"
          style={{ backgroundColor: 'rgba(0,0,0,0.2)', color: 'inherit', border: '2px solid currentColor' }}
        >
          {data.button_text}
        </Link>
      )}
    </div>
  );
}

function BlockCards({ data }) {
  const colMap = { 2: 'grid-cols-1 sm:grid-cols-2', 3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3', 4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' };
  return (
    <div className="space-y-6">
      {data.title && (
        <div className="text-center">
          <h3 className="text-2xl font-black text-ink" style={{ fontFamily: 'Outfit, sans-serif' }}>{data.title}</h3>
          {data.subtitle && <p className="text-ink/60 mt-1">{data.subtitle}</p>}
        </div>
      )}
      <div className={`grid gap-4 ${colMap[data.columns || 3] || colMap[3]}`}>
        {(data.cards || []).map((card, i) => (
          <div key={i} className="bg-ink/[0.04] border border-ink/10 rounded-2xl p-6 hover:border-honey/30 transition-all">
            <h4 className="text-ink font-bold text-lg mb-2">{card.title}</h4>
            {card.description && <p className="text-ink/70 text-sm leading-relaxed">{card.description}</p>}
            {card.link && (
              <Link to={card.link} className="text-honey-700 text-sm font-medium mt-3 inline-block hover:underline">
                Learn more →
              </Link>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function BlockAccordion({ data }) {
  const [open, setOpen] = useState(null);
  return (
    <div className="space-y-4">
      {data.title && (
        <h3 className="text-2xl font-black text-ink" style={{ fontFamily: 'Outfit, sans-serif' }}>{data.title}</h3>
      )}
      <div className="space-y-2">
        {(data.items || []).map((item, i) => (
          <div key={i} className={`border rounded-xl overflow-hidden transition-all ${open === i ? 'border-honey/30 bg-honey/5' : 'border-ink/10 bg-ink/[0.04]'}`}>
            <button
              onClick={() => setOpen(open === i ? null : i)}
              className="w-full flex items-center justify-between px-5 py-4 text-left"
            >
              <span className="text-ink font-medium">{item.question}</span>
              <ChevronDown
                className={`w-4 h-4 text-ink/55 flex-shrink-0 ml-4 transition-transform ${open === i ? 'rotate-180 text-honey-700' : ''}`}
              />
            </button>
            {open === i && (
              <div className="px-5 pb-4 text-ink/70 text-sm leading-relaxed border-t border-ink/10 pt-3">
                {item.answer}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function BlockColumns({ data }) {
  const count = data.columns_count || 2;
  const colMap = { 2: 'grid-cols-1 md:grid-cols-2', 3: 'grid-cols-1 md:grid-cols-3' };
  return (
    <div className={`grid gap-8 ${colMap[count] || colMap[2]}`}>
      {(data.columns || []).slice(0, count).map((col, i) => (
        <p key={i} className="text-ink/75 leading-relaxed whitespace-pre-wrap">
          {col.content}
        </p>
      ))}
    </div>
  );
}

function BlockDivider({ data }) {
  const styleMap = { solid: 'border-solid', dashed: 'border-dashed', dotted: 'border-dotted' };
  const marginMap = { sm: 'my-4', md: 'my-8', lg: 'my-12', xl: 'my-20' };
  return (
    <div className={`${marginMap[data.margin || 'md']}`}>
      {data.label ? (
        <div className="flex items-center gap-4">
          <hr className={`flex-1 border-t border-ink/10 ${styleMap[data.style || 'solid']}`} />
          <span className="text-ink/45 text-sm font-medium">{data.label}</span>
          <hr className={`flex-1 border-t border-ink/10 ${styleMap[data.style || 'solid']}`} />
        </div>
      ) : (
        <hr className={`border-t border-ink/10 ${styleMap[data.style || 'solid']}`} />
      )}
    </div>
  );
}

function BlockSpacer({ data }) {
  const sizeMap = { xs: 'h-4', sm: 'h-8', md: 'h-12', lg: 'h-20', xl: 'h-32' };
  return <div className={sizeMap[data.size || 'md']} />;
}

function BlockHTML({ data }) {
  return (
    <div
      className="text-ink/75"
      dangerouslySetInnerHTML={{ __html: data.content || '' }}
    />
  );
}

function BlockButton({ data }) {
  if (!data.text) return null;
  const alignMap = { left: 'justify-start', center: 'justify-center', right: 'justify-end' };
  const sizeMap = { sm: 'px-4 py-2 text-sm', md: 'px-6 py-3 text-sm', lg: 'px-8 py-4 text-base' };
  const variantMap = {
    primary: 'bg-honey text-black hover:bg-honey-600',
    secondary: 'bg-ink/[0.07] text-ink hover:bg-ink/10',
    outline: 'border-2 border-honey text-honey-700 hover:bg-honey/10',
    ghost: 'text-honey-700 hover:bg-honey/10',
  };
  const cls = `inline-flex items-center justify-center rounded-full font-bold transition-all hover:scale-105 ${sizeMap[data.size] || sizeMap.md} ${variantMap[data.variant] || variantMap.primary} ${data.full_width ? 'w-full' : ''}`;
  const isExternal = /^https?:\/\//.test(data.link || '');
  return (
    <div className={`flex ${data.full_width ? '' : (alignMap[data.align] || 'justify-start')}`}>
      {isExternal ? (
        <a href={data.link} target="_blank" rel="noopener noreferrer" className={cls}>{data.text}</a>
      ) : (
        <Link to={data.link || '/'} className={cls}>{data.text}</Link>
      )}
    </div>
  );
}

function BlockBadges({ data }) {
  const alignMap = { left: 'justify-start', center: 'justify-center', right: 'justify-end' };
  const colorMap = {
    cyan: 'bg-honey/10 text-honey-700 border-honey/30',
    gold: 'bg-honey/10 text-honey-600 border-honey/30',
    green: 'bg-green-500/10 text-green-600 border-green-500/30',
    red: 'bg-red-500/10 text-red-600 border-red-500/30',
    white: 'bg-ink/[0.07] text-ink border-ink/15',
  };
  return (
    <div className={`flex flex-wrap gap-2 ${alignMap[data.align] || 'justify-start'}`}>
      {(data.items || []).map((item, i) => (
        <span key={i} className={`px-3 py-1 rounded-full text-sm font-semibold border ${colorMap[data.variant] || colorMap.cyan}`}>
          {item.label}
        </span>
      ))}
    </div>
  );
}

function BlockAlert({ data }) {
  const variantMap = {
    info: 'bg-honey/5 border-honey/30 text-honey-700',
    success: 'bg-green-500/5 border-green-500/30 text-green-600',
    warning: 'bg-honey/5 border-honey/30 text-honey-600',
    error: 'bg-red-500/5 border-red-500/30 text-red-600',
  };
  const iconMap = { info: Info, success: CheckCircle2, warning: AlertTriangle, error: XCircle };
  const Icon = iconMap[data.variant] || Info;
  return (
    <div className={`rounded-xl border px-5 py-4 flex gap-3 ${variantMap[data.variant] || variantMap.info}`}>
      <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
      <div>
        {data.title && <p className="font-bold mb-1">{data.title}</p>}
        {data.content && <p className="text-ink/75 text-sm leading-relaxed whitespace-pre-wrap">{data.content}</p>}
      </div>
    </div>
  );
}

function BlockTabs({ data }) {
  const items = data.items || [];
  if (!items.length) return null;
  return (
    <Tabs defaultValue="tab-0" className="w-full">
      <TabsList className="bg-ink/[0.04] border border-ink/10 flex-wrap h-auto">
        {items.map((item, i) => (
          <TabsTrigger key={i} value={`tab-${i}`}
            className="data-[state=active]:bg-honey/10 data-[state=active]:text-honey-700 text-ink/60">
            {item.label || `Tab ${i + 1}`}
          </TabsTrigger>
        ))}
      </TabsList>
      {items.map((item, i) => (
        <TabsContent key={i} value={`tab-${i}`} className="mt-4 text-ink/75 leading-relaxed whitespace-pre-wrap">
          {item.content}
        </TabsContent>
      ))}
    </Tabs>
  );
}

function BlockCarousel({ data }) {
  const images = (data.images || []).filter(im => im.src);
  if (!images.length) return null;
  return (
    <Carousel className="w-full max-w-2xl mx-auto" opts={{ loop: true }}>
      <CarouselContent>
        {images.map((img, i) => (
          <CarouselItem key={i}>
            <figure>
              <img src={img.src} alt={img.alt || ''} className="w-full rounded-2xl border border-ink/10 object-cover aspect-video" />
              {img.caption && <figcaption className="text-ink/55 text-sm text-center mt-2">{img.caption}</figcaption>}
            </figure>
          </CarouselItem>
        ))}
      </CarouselContent>
      {data.show_arrows !== false && images.length > 1 && (
        <>
          <CarouselPrevious className="bg-ink/[0.07] border-ink/10 text-ink hover:bg-honey hover:text-black" />
          <CarouselNext className="bg-ink/[0.07] border-ink/10 text-ink hover:bg-honey hover:text-black" />
        </>
      )}
    </Carousel>
  );
}

function BlockTable({ data }) {
  const columns = data.columns || [];
  const rows = data.rows || [];
  if (!columns.length) return null;
  return (
    <div className="rounded-2xl border border-ink/10 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="border-ink/10 hover:bg-transparent">
            {columns.map((col, i) => (
              <TableHead key={i} className="text-honey-700 font-bold uppercase text-xs tracking-wider px-4">{col}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, ri) => (
            <TableRow key={ri} className={`border-ink/10 hover:bg-ink/[0.04] ${data.striped !== false && ri % 2 ? 'bg-ink/[0.02]' : ''}`}>
              {columns.map((_, ci) => (
                <TableCell key={ci} className="text-ink/75 px-4 py-3">{row.cells?.[ci] || ''}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function BlockProgress({ data }) {
  return (
    <div className="space-y-5">
      {data.title && <h3 className="text-2xl font-black text-ink" style={{ fontFamily: 'Outfit, sans-serif' }}>{data.title}</h3>}
      <div className="space-y-4">
        {(data.items || []).map((item, i) => (
          <div key={i} className="space-y-1.5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-ink/75 font-medium">{item.label}</span>
              <span className="text-honey-700 font-mono">{item.value ?? 0}%</span>
            </div>
            <Progress value={item.value ?? 0} className="h-2 bg-ink/[0.07] [&>div]:bg-honey" />
          </div>
        ))}
      </div>
    </div>
  );
}

function BlockTestimonial({ data }) {
  const initials = (data.author || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <figure className="bg-ink/[0.04] border border-ink/10 rounded-2xl p-8 max-w-2xl mx-auto text-center space-y-4">
      <Quote className="w-8 h-8 text-honey-700/40 mx-auto" />
      <blockquote className="text-ink/85 text-lg leading-relaxed italic">"{data.quote}"</blockquote>
      <figcaption className="flex items-center justify-center gap-3 pt-2">
        <Avatar className="w-12 h-12 border border-ink/10">
          {data.avatar_src && <AvatarImage src={data.avatar_src} alt={data.author || ''} />}
          <AvatarFallback className="bg-honey/10 text-honey-700 font-bold">{initials}</AvatarFallback>
        </Avatar>
        <div className="text-left">
          {data.author && <p className="text-ink font-bold text-sm">{data.author}</p>}
          {data.role && <p className="text-ink/55 text-xs">{data.role}</p>}
        </div>
      </figcaption>
    </figure>
  );
}

const BLOCK_RENDERERS = {
  heading: BlockHeading,
  paragraph: BlockParagraph,
  image: BlockImage,
  image_text: BlockImageText,
  video: BlockVideo,
  cta: BlockCTA,
  cards: BlockCards,
  accordion: BlockAccordion,
  columns: BlockColumns,
  divider: BlockDivider,
  spacer: BlockSpacer,
  button: BlockButton,
  badges: BlockBadges,
  alert: BlockAlert,
  tabs: BlockTabs,
  carousel: BlockCarousel,
  table: BlockTable,
  progress: BlockProgress,
  testimonial: BlockTestimonial,
  html: BlockHTML,
};

// ── Main DynamicPage ─────────────────────────────────────────────
export default function DynamicPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await axios.get(`${API}/pages/${slug}`);
        setPage(res.data);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [slug]);

  if (loading) {
    return (
      <div className="store-page flex items-center justify-center">
        <Navbar />
        <div className="w-8 h-8 border-2 border-honey border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !page) {
    return (
      <div className="store-page">
        <Navbar />
        <div className="flex flex-col items-center justify-center min-h-[70vh] text-center px-4">
          <p className="text-7xl font-black text-ink/15 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>404</p>
          <h1 className="text-3xl font-black text-ink mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Page Not Found</h1>
          <p className="text-ink/55 mb-8">The page <code className="text-honey-700">/pages/{slug}</code> doesn't exist or isn't published yet.</p>
          <Link to="/" className="px-6 py-3 bg-honey text-black font-bold rounded-full hover:bg-honey-600 transition-colors">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="store-page">
      <Navbar />

      {/* Page header */}
      <div className="relative pt-10 pb-10 text-center px-4 border-b border-ink/5">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-4xl sm:text-5xl font-black text-ink"
          style={{ fontFamily: 'Outfit, sans-serif' }}
          data-testid="dynamic-page-title"
        >
          {page.title}
        </motion.h1>
      </div>

      {/* Blocks */}
      <div className="max-w-4xl mx-auto px-4 py-12 space-y-10">
        {(page.blocks || []).map((block, i) => {
          const Renderer = BLOCK_RENDERERS[block.type];
          if (!Renderer) return null;
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              data-testid={`page-block-${block.type}-${i}`}
            >
              <Renderer data={block.data || {}} />
            </motion.div>
          );
        })}

        {page.blocks?.length === 0 && (
          <div className="text-center py-20 text-ink/30">
            <p>This page has no content yet.</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-ink/5 mt-20 py-8 text-center">
        <p className="text-ink/30 text-sm">
          &copy; {new Date().getFullYear()} Market @ Buzzzillion
        </p>
      </footer>
    </div>
  );
}
